import { reactive } from "vue";

export type ManageActionType = "approve" | "deny" | "modify";

export interface ManageActionPromptOptions {
  action: ManageActionType;
  title: string;
  message: string;
  icon?: "question" | "warning" | "info";
  confirmButtonText: string;
  confirmButtonColor?: string;
}

export interface ManageActionPromptResult {
  isConfirmed: boolean;
  ticket_id: number | null;
  extra_info: string | null;
}

/**
 * Reactive singleton driving the shared <ManageActionModal /> mounted once in App.vue.
 * `promptManageAction` opens the modal and returns a Promise; the modal resolves it via
 * `resolveManageAction` on confirm/cancel — so callers keep the simple `await` ergonomics.
 */
export const manageActionState = reactive<{
  open: boolean;
  opts: ManageActionPromptOptions | null;
}>({
  open: false,
  opts: null,
});

let resolver: ((result: ManageActionPromptResult) => void) | null = null;

/**
 * Shared confirmation dialog for the Approve / Deny / Modify actions on the Krakend,
 * OneClick and Application demand listings. It confirms the action AND collects a
 * Ticket ID (integer) and Extra Info (free text), enforcing the per-action required
 * rules client-side before the caller proceeds with the API request.
 */
export function promptManageAction(
  opts: ManageActionPromptOptions,
): Promise<ManageActionPromptResult> {
  manageActionState.opts = opts;
  manageActionState.open = true;
  return new Promise((resolve) => {
    resolver = resolve;
  });
}

export function resolveManageAction(result: ManageActionPromptResult): void {
  manageActionState.open = false;
  const resolve = resolver;
  resolver = null;
  if (resolve) resolve(result);
}
